import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { UserRole } from '../lib/profiles';

interface UseIsAdminResult {
  isAdmin: boolean;
  isLoading: boolean;
  role: UserRole | null;
}

// Session cache for admin status
const adminCache: { 
  userId: string | null; 
  isAdmin: boolean; 
  role: UserRole | null;
  checked: boolean;
} = {
  userId: null,
  isAdmin: false,
  role: null,
  checked: false,
};

/**
 * Hook to check if the current user is an admin
 * Caches the result for the session to avoid repeated DB calls
 */
export function useIsAdmin(): UseIsAdminResult {
  const [isAdmin, setIsAdmin] = useState<boolean>(adminCache.checked ? adminCache.isAdmin : false);
  const [isLoading, setIsLoading] = useState<boolean>(!adminCache.checked);
  const [role, setRole] = useState<UserRole | null>(adminCache.role);

  useEffect(() => {
    let isMounted = true;

    const checkAdminStatus = async () => {
      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          if (isMounted) {
            setIsAdmin(false);
            setRole(null);
            setIsLoading(false);
          }
          return;
        }

        // Check cache first
        if (adminCache.checked && adminCache.userId === user.id) {
          if (isMounted) {
            setIsAdmin(adminCache.isAdmin);
            setRole(adminCache.role);
            setIsLoading(false);
          }
          return;
        }

        // Fetch user's role from profiles table
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('[useIsAdmin] Error fetching role:', error);
          if (isMounted) {
            setIsAdmin(false);
            setRole(null);
            setIsLoading(false);
          }
          return;
        }

        const userRole = (profile?.role as UserRole) || 'user';
        const isUserAdmin = userRole === 'admin';

        // Update cache
        adminCache.userId = user.id;
        adminCache.isAdmin = isUserAdmin;
        adminCache.role = userRole;
        adminCache.checked = true;

        if (isMounted) {
          setIsAdmin(isUserAdmin);
          setRole(userRole);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('[useIsAdmin] Unexpected error:', error);
        if (isMounted) {
          setIsAdmin(false);
          setRole(null);
          setIsLoading(false);
        }
      }
    };

    checkAdminStatus();

    // Listen for auth changes to invalidate cache
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        // Clear cache on logout
        adminCache.userId = null;
        adminCache.isAdmin = false;
        adminCache.role = null;
        adminCache.checked = false;
        
        if (isMounted) {
          setIsAdmin(false);
          setRole(null);
        }
      } else if (event === 'SIGNED_IN') {
        // Re-check on sign in
        adminCache.checked = false;
        checkAdminStatus();
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return { isAdmin, isLoading, role };
}

/**
 * Clear the admin cache (call when user logs out)
 */
export function clearAdminCache(): void {
  adminCache.userId = null;
  adminCache.isAdmin = false;
  adminCache.role = null;
  adminCache.checked = false;
}

